/**
 * Theme utility functions for consistent theme application across components
 */

/**
 * Get theme-aware CSS classes for common UI elements
 */
export const getThemeClasses = () => {
  return {
    // Background classes
    bg: {
      primary: 'bg-white dark:bg-gray-800',
      secondary: 'bg-gray-50 dark:bg-gray-900',
      tertiary: 'bg-gray-100 dark:bg-gray-700',
      card: 'bg-white dark:bg-gray-800',
      overlay: 'bg-black/20 dark:bg-black/40',
      input: 'bg-white dark:bg-gray-700',
      button: {
        primary: 'bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600',
        secondary: 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600',
        danger: 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
        success: 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600'
      }
    },
    
    // Text classes
    text: {
      primary: 'text-gray-900 dark:text-white',
      secondary: 'text-gray-600 dark:text-gray-300',
      tertiary: 'text-gray-500 dark:text-gray-400',
      muted: 'text-gray-400 dark:text-gray-500',
      accent: 'text-teal-600 dark:text-teal-400',
      error: 'text-red-600 dark:text-red-400',
      success: 'text-green-600 dark:text-green-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      link: 'text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300'
    },
    
    // Border classes
    border: {
      primary: 'border-gray-200 dark:border-gray-700',
      secondary: 'border-gray-300 dark:border-gray-600',
      input: 'border-gray-300 dark:border-gray-600 focus:border-teal-500 dark:focus:border-teal-400',
      error: 'border-red-300 dark:border-red-600',
      success: 'border-green-300 dark:border-green-600'
    },
    
    // Ring/focus classes
    ring: {
      primary: 'focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800',
      error: 'focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800'
    },
    
    // Placeholder classes
    placeholder: 'placeholder-gray-500 dark:placeholder-gray-400',
    
    // Shadow classes
    shadow: {
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      card: 'shadow-sm hover:shadow-md dark:shadow-gray-900/20'
    }
  };
};

/**
 * Get theme-aware classes for form inputs
 */
export const getInputClasses = (hasError = false, disabled = false) => {
  const theme = getThemeClasses();
  
  let classes = [
    'w-full px-3 py-2 rounded-lg transition-colors border',
    theme.bg.input,
    theme.text.primary,
    theme.placeholder,
    'focus:outline-none'
  ];
  
  if (hasError) {
    classes.push(theme.border.error, theme.ring.error);
  } else {
    classes.push(theme.border.input, theme.ring.primary);
  }
  
  if (disabled) {
    classes.push('opacity-50 cursor-not-allowed');
  }
  
  return classes.join(' ');
};

/**
 * Get theme-aware classes for buttons
 */
export const getButtonClasses = (variant = 'primary', size = 'medium', disabled = false) => {
  const theme = getThemeClasses();
  
  let classes = [
    'inline-flex items-center justify-center font-medium rounded-lg transition-colors',
    'focus:outline-none',
    theme.ring.primary
  ];
  
  // Size classes
  switch (size) {
    case 'small':
      classes.push('px-3 py-1.5 text-sm');
      break;
    case 'large':
      classes.push('px-6 py-3 text-lg');
      break;
    default:
      classes.push('px-4 py-2 text-sm');
  }
  
  // Variant classes
  switch (variant) {
    case 'secondary':
      classes.push(theme.bg.button.secondary, 'text-white');
      break;
    case 'danger':
      classes.push(theme.bg.button.danger, 'text-white');
      break;
    case 'success':
      classes.push(theme.bg.button.success, 'text-white');
      break;
    case 'outline':
      classes.push(
        'border-2',
        theme.border.primary,
        theme.text.primary,
        'hover:bg-gray-50 dark:hover:bg-gray-700'
      );
      break;
    case 'ghost':
      classes.push(
        theme.text.primary,
        'hover:bg-gray-100 dark:hover:bg-gray-700'
      );
      break;
    default:
      classes.push(theme.bg.button.primary, 'text-white');
  }
  
  if (disabled) {
    classes.push('opacity-50 cursor-not-allowed');
  }
  
  return classes.join(' ');
};

/**
 * Get theme-aware classes for cards
 */
export const getCardClasses = (interactive = false) => {
  const theme = getThemeClasses();
  
  let classes = [
    'rounded-lg border',
    theme.bg.card,
    theme.border.primary
  ];
  
  if (interactive) {
    classes.push(theme.shadow.card, 'transition-shadow');
  } else {
    classes.push(theme.shadow.sm);
  }
  
  return classes.join(' ');
};

/**
 * Get theme-aware classes for alerts/notifications
 */
export const getAlertClasses = (type = 'info') => {
  const baseClasses = 'rounded-lg border p-4';
  
  switch (type) {
    case 'error':
      return `${baseClasses} bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300`;
    case 'success':
      return `${baseClasses} bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300`;
    case 'warning':
      return `${baseClasses} bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300`;
    default:
      return `${baseClasses} bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300`;
  }
};

/**
 * Get theme-aware classes for loading states
 */
export const getLoadingClasses = () => {
  return {
    spinner: 'animate-spin rounded-full border-2 border-gray-300 border-t-teal-500 dark:border-gray-600 dark:border-t-teal-400',
    skeleton: 'animate-pulse bg-gray-300 dark:bg-gray-600 rounded',
    overlay: 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm'
  };
};

/**
 * Apply theme classes to an element dynamically
 */
export const applyThemeClasses = (element, classMap) => {
  if (!element || !classMap) return;
  
  Object.entries(classMap).forEach(([key, classes]) => {
    if (typeof classes === 'string') {
      element.classList.add(...classes.split(' '));
    }
  });
};

/**
 * Remove theme classes from an element
 */
export const removeThemeClasses = (element, classMap) => {
  if (!element || !classMap) return;
  
  Object.entries(classMap).forEach(([key, classes]) => {
    if (typeof classes === 'string') {
      element.classList.remove(...classes.split(' '));
    }
  });
};

/**
 * Check if current theme is dark
 */
export const isDarkTheme = () => {
  return document.documentElement.classList.contains('dark') ||
         document.documentElement.getAttribute('data-theme') === 'dark';
};

/**
 * Get current theme
 */
export const getCurrentTheme = () => {
  return isDarkTheme() ? 'dark' : 'light';
};

/**
 * Ensure theme consistency across the application
 */
export const ensureThemeConsistency = () => {
  const theme = getCurrentTheme();
  
  // Ensure root element has correct theme classes
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(theme);
  document.documentElement.setAttribute('data-theme', theme);
  
  // Dispatch custom event for components that need to react to theme changes
  window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme } }));
};

/**
 * Theme-aware class builder utility
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};
